import React, { useMemo, useCallback, useRef, useEffect, useLayoutEffect, useState, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { useConnectionMonitor } from '../../contexts/ConnectionMonitorContext';
import ReactQuill, { Quill } from 'react-quill';
import {
  ArrowLeft, Save, Loader2, CheckCircle2,
  Undo2, Redo2, Bold, Italic, Underline, Strikethrough,
  Highlighter, X, ListOrdered, List,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Link2, Quote, Code, Minus, ChevronDown, Type,
  Heading, Printer, Share2, FileDown,
} from 'lucide-react';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import 'react-quill/dist/quill.snow.css';

/* ─── Quill format registrations ─── */
try {
  const SizeStyle = Quill.import('attributors/style/size');
  SizeStyle.whitelist = ['10px','11px','12px','13px','14px','15px','16px','18px','20px','22px','24px','28px','32px','36px','48px','64px','72px'];
  Quill.register(SizeStyle, true);
} catch (_) {}

try {
  const FontStyle = Quill.import('attributors/style/font');
  FontStyle.whitelist = null; // allow any
  Quill.register(FontStyle, true);
} catch (_) {}

/* HR/Divider blot */
try {
  const BlockEmbed = Quill.import('blots/block/embed');
  class DividerBlot extends BlockEmbed {
    static create() { return document.createElement('hr'); }
    static value() { return true; }
  }
  DividerBlot.blotName = 'hr';
  DividerBlot.tagName = 'hr';
  Quill.register(DividerBlot, true);
} catch (_) {}

const getHeadingOptions = (t) => [
  { label: t('common:normal'), value: false },
  { label: `${t('editor.heading')} 1`, value: 1 },
  { label: `${t('editor.heading')} 2`, value: 2 },
  { label: `${t('editor.heading')} 3`, value: 3 },
  { label: `${t('editor.heading')} 4`, value: 4 },
];

const SIZE_OPTIONS = [
  { label: '10', value: '10px' },
  { label: '11', value: '11px' },
  { label: '12', value: '12px' },
  { label: '13', value: '13px' },
  { label: '14', value: '14px' },
  { label: '15', value: '15px' },
  { label: '16', value: '16px' },
  { label: '18', value: '18px' },
  { label: '20', value: '20px' },
  { label: '22', value: '22px' },
  { label: '24', value: '24px' },
  { label: '28', value: '28px' },
  { label: '32', value: '32px' },
  { label: '36', value: '36px' },
  { label: '48', value: '48px' },
];

const FONT_OPTIONS = [
  // Sans-serif
  { label: 'Inter', value: 'Inter, sans-serif' },
  { label: 'Roboto', value: 'Roboto, sans-serif' },
  { label: 'Open Sans', value: "'Open Sans', sans-serif" },
  { label: 'Lato', value: 'Lato, sans-serif' },
  { label: 'Nunito', value: 'Nunito, sans-serif' },
  { label: 'Montserrat', value: 'Montserrat, sans-serif' },
  { label: 'Poppins', value: 'Poppins, sans-serif' },
  // Serif
  { label: 'Playfair Display', value: "'Playfair Display', serif" },
  { label: 'Merriweather', value: 'Merriweather, serif' },
  { label: 'Lora', value: 'Lora, serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: "'Times New Roman', serif" },
  // Monospace
  { label: 'Source Code Pro', value: "'Source Code Pro', monospace" },
  { label: 'JetBrains Mono', value: "'JetBrains Mono', monospace" },
  { label: 'Fira Code', value: "'Fira Code', monospace" },
  { label: 'Courier New', value: "'Courier New', monospace" },
  // System
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Tahoma', value: 'Tahoma, sans-serif' },
  { label: 'Segoe UI', value: "'Segoe UI', sans-serif" },
];

const TEXT_COLORS = [
  '#ffffff','#e4edf7','#94adc8','#64748b','#374151','#111827',
  '#ef4444','#f97316','#eab308','#22c55e','#14b8a6','#3b82f6','#8b5cf6','#ec4899',
  '#fca5a5','#fdba74','#fde68a','#86efac','#5eead4','#93c5fd','#c4b5fd','#f9a8d4',
  '#3d8ef0','#5ca3f5','#1d4ed8','#7c3aed','#db2777','#dc2626','#d97706','#15803d',
];
const BG_COLORS = [
  'transparent',
  'rgba(61,142,240,0.2)','rgba(34,197,94,0.2)','rgba(234,179,8,0.2)',
  'rgba(239,68,68,0.2)','rgba(139,92,246,0.2)','rgba(236,72,153,0.2)',
  'rgba(20,184,166,0.2)','rgba(249,115,22,0.2)',
  '#3d8ef1','#161f31','#0d1424',
  '#1d3557','#14532d','#422006','#450a0a','#2e1065',
];

/* ─── Sub-components ─── */
const QuillEditor = forwardRef((props, ref) => <ReactQuill ref={ref} {...props} />);
QuillEditor.displayName = 'QuillEditor';

const EDITOR_FORMATS = [
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'header', 'font', 'size',
  'list', 'align',
  'link', 'blockquote', 'code-block',
  'hr', 'image',
];

class QuillLoadErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error) { if (this.props.onError) this.props.onError(error); }
  render() {
    if (this.state.hasError) return this.props.fallback || null;
    return this.props.children;
  }
}

/* Simple toolbar button */
const TBtn = ({ onClick, active, disabled, title, children, className = '' }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onMouseDown={(e) => { e.preventDefault(); onClick && onClick(); }}
    className={`
      flex items-center justify-center w-7 h-7 rounded-md text-sm transition-colors flex-shrink-0
      ${active
        ? 'bg-[#2563eb]/20 text-[#3d8ef0]'
        : 'text-[#94adc8] hover:text-[#e4edf7] hover:bg-white/[0.07]'}
      ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
      ${className}
    `}
  >
    {children}
  </button>
);

/* Toolbar divider */
const TDiv = () => <div className="w-px h-5 bg-white/[0.1] mx-0.5 flex-shrink-0" />;

/* Dropdown component */
const ToolbarDropdown = ({ label, value, options, onChange, width = 'w-32', renderOption, renderLabel }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className={`relative flex-shrink-0 ${width}`}>
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); setOpen(o => !o); }}
        className="w-full h-7 px-2 flex items-center justify-between gap-1 rounded-md text-xs text-[#c8d8eb] hover:bg-white/[0.07] hover:text-[#e4edf7] transition-colors border border-transparent hover:border-white/[0.08] truncate"
      >
        <span className="truncate">{renderLabel ? renderLabel(value) : (label || value || '—')}</span>
        <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-60" />
      </button>
      {open && createPortal(
        <DropdownPanel
          anchorRef={ref}
          options={options}
          currentValue={value}
          renderOption={renderOption}
          onSelect={(v) => { onChange(v); setOpen(false); }}
          onClose={() => setOpen(false)}
        />,
        document.body
      )}
    </div>
  );
};

const DropdownPanel = ({ anchorRef, options, currentValue, renderOption, onSelect, onClose }) => {
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, maxH: 300 });

  useEffect(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const panelH = Math.min(300, options.length * 32 + 8);
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    let top, maxH;
    if (spaceBelow >= panelH || spaceBelow >= spaceAbove) {
      top = rect.bottom + 4;
      maxH = spaceBelow;
    } else {
      maxH = spaceAbove;
      top = rect.top - Math.min(panelH, maxH) - 4;
    }
    setPos({ top, left: rect.left, maxH: Math.min(300, maxH) });
  }, [anchorRef, options.length]);

  useEffect(() => {
    const handler = (e) => { if (!panelRef.current?.contains(e.target) && !anchorRef.current?.contains(e.target)) onClose(); };
    const key = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('keydown', key); };
  }, [anchorRef, onClose]);

  return (
    <div
      ref={panelRef}
      style={{ top: pos.top, left: pos.left, maxHeight: pos.maxH, minWidth: anchorRef.current?.offsetWidth || 120 }}
      className="fixed z-[99999] bg-[#0d1424] border border-white/[0.09] rounded-xl shadow-[0_16px_48px_rgba(0,0,0,0.7)] overflow-y-auto py-1 scrollbar-thin"
    >
      {options.map((opt, i) => (
        <button
          key={i}
          type="button"
          onMouseDown={(e) => { e.preventDefault(); onSelect(opt.value); }}
          className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center gap-2
            ${opt.value === currentValue
              ? 'bg-[#2563eb]/15 text-[#3d8ef0]'
              : 'text-[#c8d8eb] hover:bg-white/[0.05] hover:text-[#e4edf7]'
            }`}
        >
          {renderOption ? renderOption(opt) : opt.label}
        </button>
      ))}
    </div>
  );
};

/* Color picker popover */
const ColorPicker = ({ colors, value, onChange, onClear, triggerIcon, title }) => {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!btnRef.current?.contains(e.target) && !document.getElementById('color-panel-portal')?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = (e) => {
    e.preventDefault();
    if (open) { setOpen(false); return; }
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) {
      const panelW = 208; // w-52 = 13rem ≈ 208px
      let left = rect.left;
      if (left + panelW > window.innerWidth - 8) left = window.innerWidth - panelW - 8;
      setPanelPos({ top: rect.bottom + 6, left: Math.max(8, left) });
    }
    setOpen(true);
  };

  return (
    <div className="relative flex-shrink-0">
      <button
        ref={btnRef}
        type="button"
        title={title}
        onMouseDown={handleOpen}
        className="flex flex-col items-center justify-center w-7 h-7 rounded-md text-[#94adc8] hover:text-[#e4edf7] hover:bg-white/[0.07] transition-colors cursor-pointer"
      >
        {triggerIcon}
        <div
          className="w-4 h-[3px] rounded-full mt-0.5"
          style={{ backgroundColor: value && value !== 'transparent' ? value : 'transparent', border: (!value || value === 'transparent') ? '1px solid rgba(148,173,200,0.4)' : 'none' }}
        />
      </button>
      {open && createPortal(
        <div id="color-panel-portal" style={{ position: 'fixed', top: panelPos.top, left: panelPos.left, zIndex: 99999 }}>
          <ColorPanel
            colors={colors}
            value={value}
            onSelect={(c) => { onChange(c); setOpen(false); }}
            onClear={() => { onClear(); setOpen(false); }}
            onCustom={(c) => { onChange(c); }}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

const ColorPanel = ({ colors, value, onSelect, onClear, onCustom }) => {
  const { t } = useTranslation('notes');
  const nativeInputRef = useRef(null);

  const openNativePicker = () => {
    nativeInputRef.current?.click();
  };

  return (
    <div className="bg-[#0d1424] border border-white/[0.09] rounded-xl shadow-[0_16px_48px_rgba(0,0,0,0.7)] p-3 w-52">
      <div className="grid grid-cols-8 gap-1 mb-2">
        {colors.map((c, i) => (
          <button
            key={i}
            type="button"
            title={c}
            onMouseDown={(e) => { e.preventDefault(); onSelect(c); }}
            style={{ backgroundColor: c === 'transparent' ? undefined : c }}
            className={`w-5 h-5 rounded-md border transition-transform hover:scale-110 flex-shrink-0 ${
              c === 'transparent'
                ? 'border-white/20 bg-[url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'4\' height=\'4\'%3E%3Crect width=\'2\' height=\'2\' fill=\'%23888\'/%3E%3Crect x=\'2\' y=\'2\' width=\'2\' height=\'2\' fill=\'%23888\'/%3E%3C/svg%3E")]'
                : c === value ? 'border-[#3d8ef0] ring-1 ring-[#3d8ef0]' : 'border-white/10 hover:border-white/30'
            }`}
          />
        ))}
      </div>
      <div className="flex gap-1.5 items-center">
        {/* hidden native color input */}
        <input
          ref={nativeInputRef}
          type="color"
          defaultValue={value && value !== 'transparent' ? value : '#3d8ef0'}
          onChange={(e) => onCustom(e.target.value)}
          style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
          tabIndex={-1}
        />
        <button
          type="button"
          onClick={openNativePicker}
          className="flex-1 flex items-center justify-center gap-1.5 text-xs text-[#c8d8eb] hover:text-white bg-white/[0.06] hover:bg-white/[0.1] transition-colors px-2 py-1.5 rounded-lg border border-white/[0.08]"
        >
          <span className="w-3.5 h-3.5 rounded-sm border border-white/20 flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #ff6b6b, #ffd93d, #6bcb77, #4d96ff, #c77dff)' }}
          />
          {t('editor.customColor')}
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); onClear(); }}
          className="text-xs text-[#7a96b0] hover:text-[#e4edf7] transition-colors px-2 py-1.5 rounded-lg hover:bg-white/[0.05]"
        >
          {t('editor.clearColor')}
        </button>
      </div>
    </div>
  );
};

/* ─── Main component ─── */
const RichNoteEditor = ({
  note,
  title,
  content,
  onTitleChange,
  onContentChange,
  onBack,
  saveNote,
  readOnly = false,
  saveStatus = 'saved',
  draft = null,
  onRestoreDraft,
  onDismissDraft,
  onDelta,
  collaborationRef,
  refreshKey = 0,
}) => {
  const { t, i18n } = useTranslation('notes');
  const dateLocale = getDateFnsLocale(i18n.language);
  const headingOptions = useMemo(() => getHeadingOptions(t), [t]);
  const quillRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const titleInputRef = useRef(null);
  const latestHtmlRef = useRef(content || '');

  // ReactQuill yalnızca mount/remount anında defaultValue alır — controlled value kullanılmaz.
  // Aksi hâlde her parent re-render'da setContents() çağrılır ve scroll sıfırlanır.
  const initialContentRef = useRef(typeof content === 'string' ? content : '');
  const [quillRemountKey, setQuillRemountKey] = useState(0);

  // Parent'tan gelen dış içerik güncellemesi (taslak geri yükleme vb.) — gereksiz remount önle
  useLayoutEffect(() => {
    const nextContent = typeof content === 'string' ? content : '';
    if (nextContent === latestHtmlRef.current) return;

    const editor = quillRef.current?.getEditor();
    if (editor?.root) {
      const currentHtml = editor.root.innerHTML;
      if (currentHtml === nextContent) {
        latestHtmlRef.current = nextContent;
        return;
      }
    }

    initialContentRef.current = nextContent;
    latestHtmlRef.current = nextContent;
    setQuillRemountKey((k) => k + 1);
  }, [content]);
  // onDelta'yı her zaman güncel tutan ref (closure sorununu önler)
  const onDeltaRef = useRef(onDelta);
  useEffect(() => { onDeltaRef.current = onDelta; }, [onDelta]);
  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const isElectron = !!window.api;
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, target: null, selectionLength: 0, linkUrl: null });
  const contextMenuRef = useRef(null);
  const normalizedTitle = typeof title === 'string' ? title : '';
  const normalizedContent = typeof content === 'string' ? content : '';

  /* ─── Toolbar state ─── */
  const savedSel = useRef(null);
  const [activeFormats, setActiveFormats] = useState({});
  const [textColor, setTextColor] = useState('');
  const [bgColor, setBgColor] = useState('');
  const [linkModal, setLinkModal] = useState({ visible: false, url: '' });
  const savedLinkSel = useRef(null);
  const linkInputRef = useRef(null);

  /* suppress findDOMNode warnings */
  useEffect(() => {
    const orig = { warn: console.warn, error: console.error };
    const check = (args) => args.some(a => (typeof a === 'string' && a.includes('findDOMNode')) || (a?.message || '').includes('findDOMNode'));
    console.warn = (...args) => { if (!check(args)) orig.warn.apply(console, args); };
    console.error = (...args) => { if (!check(args)) orig.error.apply(console, args); };
    return () => { console.warn = orig.warn; console.error = orig.error; };
  }, []);

  /* track active formats on selection change + keep savedSel in sync */
  useEffect(() => {
    const setupListener = () => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return false;
      const update = (range) => {
        const sel = range !== undefined ? range : editor.getSelection();
        if (sel) {
          savedSel.current = sel; // always keep in sync
          const fmt = editor.getFormat(sel);
          setActiveFormats(fmt);
          setTextColor(fmt.color || '');
          setBgColor(fmt.background || '');
        }
      };
      editor.on('selection-change', update);
      editor.on('text-change', () => update(undefined));
      return true;
    };
    if (!setupListener()) {
      const t = setTimeout(setupListener, 300);
      return () => clearTimeout(t);
    }
  }, []);


  /* ─── Gerçek zamanlı ortak düzenleme: delta gönder + uygula ─── */
  useEffect(() => {
    const setup = () => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return false;

      const handleTextChange = (delta, _oldDelta, source) => {
        // Sadece kullanıcının kendi yazdıklarını gönder (uzaktan gelen deltaları tekrar yayma)
        if (source === 'user' && onDeltaRef.current) {
          onDeltaRef.current(delta);
        }
      };
      editor.on('text-change', handleTextChange);
      return () => editor.off('text-change', handleTextChange);
    };

    let cleanup;
    const result = setup();
    if (typeof result === 'function') {
      cleanup = result;
    } else if (!result) {
      const t = setTimeout(() => {
        cleanup = setup() || undefined;
      }, 300);
      return () => clearTimeout(t);
    }
    return () => { if (cleanup) cleanup(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // collaborationRef üzerinden dışarıdan delta uygulanabilmesi için applyDelta expose et
  useEffect(() => {
    if (!collaborationRef) return;
    collaborationRef.current = {
      applyDelta: (delta) => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;
        // source='api' → bu text-change handler'ımızı tetiklemez, echo olmaz
        editor.updateContents(delta, 'api');
      },
    };
    return () => {
      if (collaborationRef.current) collaborationRef.current = null;
    };
  }, [collaborationRef]);

  /* ─── Format helpers ─── */

  const applyFormat = useCallback((fmt, value) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    // Use savedSel (kept in sync by selection-change listener)
    const sel = savedSel.current;
    editor.focus();
    if (sel && sel.length > 0) {
      editor.formatText(sel.index, sel.length, fmt, value);
      editor.setSelection(sel.index, sel.length);
    } else {
      if (sel) editor.setSelection(sel.index, 0);
      editor.format(fmt, value);
    }
    requestAnimationFrame(() => {
      const newSel = editor.getSelection();
      if (newSel) {
        savedSel.current = newSel;
        setActiveFormats(editor.getFormat(newSel));
      }
    });
  }, []);

  const toggleFormat = useCallback((fmt) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const sel = savedSel.current;
    const currentFmt = sel ? editor.getFormat(sel) : editor.getFormat();
    applyFormat(fmt, !currentFmt[fmt]);
  }, [applyFormat]);

  /* List toggle — formatLine kullanmak gerekiyor (satır seviyesi format) */
  const toggleList = useCallback((listType) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    // Kaydedilmiş seçimi veya mevcut seçimi al
    const sel = savedSel.current || editor.getSelection(true);
    const currentFmt = sel ? editor.getFormat(sel.index, sel.length) : editor.getFormat();
    const newValue = currentFmt.list === listType ? false : listType;
    editor.focus();
    if (sel && sel.length > 0) {
      // Seçili alandaki tüm satırlara formatLine uygula
      editor.formatLine(sel.index, sel.length, 'list', newValue, 'user');
      editor.setSelection(sel.index, sel.length);
    } else {
      // İmleç konumundaki satıra formatLine uygula
      const index = sel ? sel.index : 0;
      editor.formatLine(index, 1, 'list', newValue, 'user');
    }
    setTimeout(() => {
      const newSel = editor.getSelection();
      if (newSel) { savedSel.current = newSel; setActiveFormats(editor.getFormat(newSel)); }
    }, 30);
  }, []);

  /* Block-level formats (blockquote, code-block) — formatLine kullan */
  const toggleBlockFormat = useCallback((fmt) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const sel = savedSel.current || editor.getSelection(true);
    const currentFmt = sel ? editor.getFormat(sel.index, sel.length) : editor.getFormat();
    const next = !currentFmt[fmt];
    editor.focus();
    const index = sel ? sel.index : 0;
    const length = sel && sel.length > 0 ? sel.length : 1;
    editor.formatLine(index, length, fmt, next, 'user');
    setTimeout(() => {
      const newSel = editor.getSelection();
      if (newSel) { savedSel.current = newSel; setActiveFormats(editor.getFormat(newSel)); }
    }, 30);
  }, []);

  const applyHeading = useCallback((value) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const sel = savedSel.current || editor.getSelection(true);
    editor.focus();
    const index = sel ? sel.index : 0;
    const length = sel && sel.length > 0 ? sel.length : 1;
    editor.formatLine(index, length, 'header', value, 'user');
    if (sel) editor.setSelection(sel.index, sel.length);
  }, []);

  const applySize = useCallback((value) => {
    applyFormat('size', value);
  }, [applyFormat]);

  const applyFont = useCallback((value) => {
    applyFormat('font', value);
  }, [applyFormat]);

  const applyAlign = useCallback((value) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const sel = savedSel.current || editor.getSelection(true);
    editor.focus();
    const index = sel ? sel.index : 0;
    const length = sel && sel.length > 0 ? sel.length : 1;
    editor.formatLine(index, length, 'align', value, 'user');
    if (sel) editor.setSelection(sel.index, sel.length);
  }, []);

  const applyTextColor = useCallback((color) => {
    setTextColor(color);
    applyFormat('color', color || false);
  }, [applyFormat]);

  const applyBgColor = useCallback((color) => {
    setBgColor(color);
    applyFormat('background', (color === 'transparent' || !color) ? false : color);
  }, [applyFormat]);

  const insertHR = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    editor.focus();
    const sel = editor.getSelection(true);
    const index = sel ? sel.index : editor.getLength();
    editor.insertEmbed(index, 'hr', true, 'user');
    editor.setSelection(index + 1, 0, 'user');
  }, []);

  const openLinkModal = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    savedLinkSel.current = editor.getSelection();
    const fmt = savedLinkSel.current ? editor.getFormat(savedLinkSel.current) : {};
    setLinkModal({ visible: true, url: fmt.link || '' });
    setTimeout(() => linkInputRef.current?.focus(), 50);
  }, []);

  const applyLink = useCallback((url) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) { setLinkModal({ visible: false, url: '' }); return; }
    const sel = savedLinkSel.current;
    requestAnimationFrame(() => {
      editor.focus();
      if (sel) editor.setSelection(sel.index, sel.length);
      if (url) {
        const finalUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
        if (sel && sel.length > 0) editor.formatText(sel.index, sel.length, 'link', finalUrl);
        else editor.format('link', finalUrl);
      } else {
        if (sel && sel.length > 0) editor.formatText(sel.index, sel.length, 'link', false);
        else editor.format('link', false);
      }
    });
    setLinkModal({ visible: false, url: '' });
  }, []);

  /* ─── Link click → new tab ─── */
  useEffect(() => {
    const setup = () => {
      const editor = quillRef.current?.getEditor();
      if (!editor?.root) return false;
      const handleClick = (e) => {
        const anchor = e.target.closest('a[href]');
        if (anchor) {
          e.preventDefault();
          const href = anchor.getAttribute('href');
          if (href) window.open(href, '_blank', 'noopener,noreferrer');
        }
      };
      editor.root.addEventListener('click', handleClick);
      return () => editor.root.removeEventListener('click', handleClick);
    };
    const cleanup = setup();
    if (!cleanup) {
      const t = setTimeout(() => { setup(); }, 500);
      return () => clearTimeout(t);
    }
    return cleanup;
  }, []);

  /* ─── Context menu ─── */
  const closeContextMenu = useCallback(() => {
    setContextMenu({ visible: false, x: 0, y: 0, target: null, selectionLength: 0 });
  }, []);

  const handleContextMenu = useCallback((event) => {
    const quill = quillRef.current?.getEditor();
    const editorRoot = quill?.root || null;
    const titleInput = titleInputRef.current;
    const isEditorArea = editorRoot && editorRoot.contains(event.target);
    const isTitleArea = titleInput && titleInput.contains(event.target);
    if (!isEditorArea && !isTitleArea) return;
    event.preventDefault();
    const target = isEditorArea ? 'editor' : 'title';
    let selectionLength = 0;
    if (target === 'editor' && quill) {
      quill.focus();
      if (quill.selection?.update) quill.selection.update(event);
      selectionLength = quill.getSelection()?.length ?? 0;
    } else if (target === 'title' && titleInput) {
      titleInput.focus();
      const { selectionStart, selectionEnd } = titleInput;
      if (typeof selectionStart === 'number') selectionLength = Math.max(0, selectionEnd - selectionStart);
    }
    const x = typeof event.clientX === 'number' ? event.clientX : 0;
    const y = typeof event.clientY === 'number' ? event.clientY : 0;
    // Tıklanan element link mi?
    const linkEl = event.target.closest('a[href]');
    const linkUrl = linkEl ? linkEl.href : null;
    setContextMenu({ visible: true, x, y, target, selectionLength, linkUrl });
  }, []);

  useEffect(() => {
    if (!contextMenu.visible) return;
    const menuEl = contextMenuRef.current;
    const padding = 6;
    const rect = menuEl?.getBoundingClientRect();
    let x = contextMenu.x, y = contextMenu.y;
    if (x + (rect?.width ?? 0) > window.innerWidth) x = Math.max(padding, window.innerWidth - (rect?.width ?? 0) - padding);
    if (y + (rect?.height ?? 0) > window.innerHeight) y = Math.max(padding, window.innerHeight - (rect?.height ?? 0) - padding);
    if (x !== contextMenu.x || y !== contextMenu.y) setContextMenu(prev => prev.visible ? { ...prev, x, y } : prev);

    const onDown = (e) => { if (!contextMenuRef.current?.contains(e.target)) closeContextMenu(); };
    const onKey = (e) => { if (e.key === 'Escape') closeContextMenu(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('scroll', closeContextMenu, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', closeContextMenu);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('scroll', closeContextMenu, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', closeContextMenu);
    };
  }, [contextMenu.visible, contextMenu.x, contextMenu.y, closeContextMenu]);

  const getTitleSelectionInfo = useCallback(() => {
    const input = titleInputRef.current;
    if (!input) return null;
    return { input, start: input.selectionStart ?? 0, end: input.selectionEnd ?? 0 };
  }, []);

  const insertPlainTextIntoEditor = useCallback((text) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const sel = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
    if (sel.length > 0) quill.deleteText(sel.index, sel.length, 'user');
    quill.insertText(sel.index, text, 'user');
    quill.removeFormat(sel.index, text.length, 'user');
    quill.setSelection(sel.index + text.length, 0, 'user');
  }, []);

  const insertHtmlIntoEditor = useCallback((html) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const sel = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
    if (sel.length > 0) quill.deleteText(sel.index, sel.length, 'user');
    quill.clipboard.dangerouslyPasteHTML(sel.index, DOMPurify.sanitize(html, { USE_PROFILES: { html: true } }), 'user');
  }, []);

  const replaceTitleSelectionWithText = useCallback((text) => {
    if (!onTitleChange) return;
    const sel = getTitleSelectionInfo();
    if (!sel) return;
    const { input, start, end } = sel;
    onTitleChange(`${normalizedTitle.slice(0, start)}${text}${normalizedTitle.slice(end)}`);
    const cursor = start + text.length;
    requestAnimationFrame(() => input.setSelectionRange(cursor, cursor));
  }, [getTitleSelectionInfo, normalizedTitle, onTitleChange]);

  const handleUndo = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    editor.focus();
    (editor.getModule('history') || editor.history)?.undo();
  }, []);
  const handleRedo = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    editor.focus();
    (editor.getModule('history') || editor.history)?.redo();
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      if (contextMenu.target === 'title') {
        const sel = getTitleSelectionInfo();
        if (!sel || sel.start === sel.end) return;
        const text = normalizedTitle.slice(sel.start, sel.end);
        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
        else document.execCommand('copy');
      } else {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        const sel = quill.getSelection();
        if (!sel || sel.length === 0) return;
        const text = quill.getText(sel.index, sel.length);
        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
        else document.execCommand('copy');
      }
    } catch (e) { console.error('Kopyalama başarısız:', e); }
    finally { closeContextMenu(); }
  }, [closeContextMenu, contextMenu.target, getTitleSelectionInfo, normalizedTitle]);

  const handleCut = useCallback(async () => {
    try {
      if (contextMenu.target === 'title') {
        const sel = getTitleSelectionInfo();
        if (!sel || sel.start === sel.end) return;
        const { start, end, input } = sel;
        const text = normalizedTitle.slice(start, end);
        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
        if (onTitleChange) {
          onTitleChange(`${normalizedTitle.slice(0, start)}${normalizedTitle.slice(end)}`);
          requestAnimationFrame(() => input.setSelectionRange(start, start));
        }
      } else {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        const sel = quill.getSelection();
        if (!sel || sel.length === 0) return;
        const text = quill.getText(sel.index, sel.length);
        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
        quill.deleteText(sel.index, sel.length, 'user');
        quill.setSelection(sel.index, 0, 'user');
      }
    } catch (e) { console.error('Kesme başarısız:', e); }
    finally { closeContextMenu(); }
  }, [closeContextMenu, contextMenu.target, getTitleSelectionInfo, normalizedTitle, onTitleChange]);

  const performPaste = useCallback(async (asPlainText) => {
    try {
      if (contextMenu.target === 'title') {
        if (!onTitleChange) return;
        if (navigator.clipboard?.readText) replaceTitleSelectionWithText(await navigator.clipboard.readText());
        else document.execCommand('paste');
        return;
      }
      let html = null, text = null;
      if (!asPlainText && navigator.clipboard?.read) {
        try {
          const items = await navigator.clipboard.read();
          for (const item of items) {
            if (item.types.includes('text/html')) { html = await (await item.getType('text/html')).text(); break; }
          }
          if (!html) {
            for (const item of items) {
              if (item.types.includes('text/plain')) { text = await (await item.getType('text/plain')).text(); break; }
            }
          }
        } catch (_) {}
      }
      if (!html && !text && navigator.clipboard?.readText) text = await navigator.clipboard.readText();
      if (html && !asPlainText) insertHtmlIntoEditor(html);
      else if (typeof text === 'string') insertPlainTextIntoEditor(text);
      else document.execCommand('paste');
    } catch (e) { console.error('Yapıştırma başarısız:', e); }
    finally { closeContextMenu(); }
  }, [closeContextMenu, contextMenu.target, insertHtmlIntoEditor, insertPlainTextIntoEditor, onTitleChange, replaceTitleSelectionWithText]);

  const handlePaste = useCallback(() => performPaste(false), [performPaste]);
  const handlePastePlain = useCallback(() => performPaste(true), [performPaste]);

  const handleSelectAll = useCallback(() => {
    try {
      if (contextMenu.target === 'title') {
        const input = titleInputRef.current;
        if (!input) return;
        input.focus();
        input.setSelectionRange(0, normalizedTitle.length);
      } else {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        quill.focus();
        quill.setSelection(0, quill.getLength(), 'user');
      }
    } finally { closeContextMenu(); }
  }, [closeContextMenu, contextMenu.target, normalizedTitle]);

  const modules = useMemo(() => ({
    toolbar: false,
    history: { delay: 1000, maxStack: 200, userOnly: true },
    clipboard: { matchVisual: false },
  }), []);

  const { isServerOnline } = useConnectionMonitor();
  // Salt okunurluk yalnızca parent kararıyla (Plus gate / donmuş ortak / çevrimdışı ortak not).
  // Çevrimdışı kişisel not düzenlenebilir kalır — değişiklikler yerel kuyruğa yazılır.
  const isEditorReadOnly = readOnly;
  const [quillLoadFailed, setQuillLoadFailed] = useState(false);

  useEffect(() => {
    if (isServerOnline && quillLoadFailed) setQuillLoadFailed(false);
  }, [isServerOnline, quillLoadFailed]);

  const handleChange = useCallback((html) => {
    latestHtmlRef.current = html;
    if (onContentChange) onContentChange(html);
  }, [onContentChange]);

  const handlePrint = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    const content = editor ? editor.root.innerHTML : latestHtmlRef.current;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head>
      <title>${normalizedTitle || t('editor.untitledFallback')}</title>
      <style>
        body { font-family: Inter, system-ui, sans-serif; padding: 2.5rem; max-width: 800px; margin: 0 auto; color: #111; line-height: 1.7; }
        h1 { font-size: 1.8rem; font-weight: 700; margin-bottom: 1.5rem; border-bottom: 2px solid #e5e7eb; padding-bottom: 0.75rem; }
        blockquote { border-left: 3px solid #3d8ef0; padding-left: 1em; color: #555; font-style: italic; }
        pre { background: #f3f4f6; padding: 1em; border-radius: 6px; font-family: monospace; }
        a { color: #3d8ef0; }
        @media print { body { padding: 0; } }
      </style>
    </head><body>
      <h1>${normalizedTitle || ''}</h1>
      ${content}
    </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 300);
  }, [normalizedTitle, t]);

  const handlePDF = useCallback(async () => {
    const showToast = (type, title, message) =>
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type, title, message } }));

    showToast('info', t('editor.pdf.preparingTitle'), t('editor.pdf.preparingMessage'));

    try {
      const html2pdf = (await import('html2pdf.js/dist/html2pdf.min.js')).default;
      const editor = quillRef.current?.getEditor();
      const htmlContent = editor ? editor.root.innerHTML : latestHtmlRef.current;

      const container = document.createElement('div');
      container.style.cssText = 'font-family:Inter,system-ui,sans-serif;padding:40px;max-width:800px;margin:0 auto;color:#111;line-height:1.7;';
      container.innerHTML = `
        <h1 style="font-size:1.8rem;font-weight:700;margin-bottom:1.5rem;border-bottom:2px solid #e5e7eb;padding-bottom:0.75rem;">${normalizedTitle || ''}</h1>
        <style>
          blockquote{border-left:3px solid #3d8ef0;padding-left:1em;color:#555;font-style:italic;margin:1em 0;}
          pre{background:#f3f4f6;padding:1em;border-radius:6px;font-family:monospace;overflow-wrap:break-word;}
          a{color:#3d8ef0;}
          ul{list-style:disc;margin-left:1.5rem;margin-bottom:1rem;}
          ol{list-style:decimal;margin-left:1.5rem;margin-bottom:1rem;}
          p{margin-bottom:0.75rem;}
          img{max-width:100%;height:auto;}
        </style>
        ${htmlContent}
      `;
      document.body.appendChild(container);

      const fileName = `${normalizedTitle || t('editor.untitledFallback')}.pdf`;
      await html2pdf()
        .set({
          margin: 0,
          filename: fileName,
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(container)
        .save();

      document.body.removeChild(container);
      showToast('success', t('editor.pdf.readyTitle'), t('editor.pdf.readyMessage', { fileName }));
    } catch (err) {
      console.error('PDF oluşturma hatası:', err);
      showToast('error', t('editor.pdf.errorTitle'), t('editor.pdf.errorMessage'));
    }
  }, [normalizedTitle, t]);

  const handleShare = useCallback(() => {
    // NotesSidebar'daki NoteShareModal'ı aç (arkadaş paylaşımı + ortak çalışma daveti)
    window.dispatchEvent(new CustomEvent('notesplus-open-share-modal', { detail: { note } }));
  }, [note]);

  const handleSave = useCallback(async () => {
    if (!saveNote) return;
    const notifyToast = (type, title, message) => {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type, title, message } }));
    };
    const backupToEmergency = () => {
      try {
        window.localStorage.setItem('EMERGENCY_BACKUP_NOTE', JSON.stringify({
          id: note?.id || null, title: normalizedTitle || '',
          content: latestHtmlRef.current || '', updatedAt: new Date().toISOString(),
        }));
      } catch (_) {}
    };
    setIsSaving(true);
    let timeoutId, savedOk = false;
    try {
      await Promise.race([
        saveNote(latestHtmlRef.current),
        new Promise((_, reject) => { timeoutId = setTimeout(() => { const e = new Error('Timeout'); e.code = 'NETWORK_TIMEOUT'; reject(e); }, 8000); }),
      ]);
      savedOk = true;
    } catch (e) {
      backupToEmergency();
      if (!isServerOnline) notifyToast('info', t('editor.offlineToast.title'), t('editor.offlineToast.message'));
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      setIsSaving(false);
      if (savedOk) {
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 2000);
        // Çevrimdışı kişisel not: kayıt yereldir, kullanıcıya açıkça söylenir
        if (!isServerOnline) notifyToast('info', t('editor.offlineToast.title'), t('editor.offlineToast.message'));
      }
    }
  }, [note, normalizedTitle, saveNote, isServerOnline, t]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (!isEditorReadOnly) handleSave();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleSave, isEditorReadOnly]);

  useEffect(() => {
    const apply = () => {
      const editor = quillRef.current?.getEditor();
      if (!editor?.root) return false;
      editor.root.setAttribute('spellcheck', 'false');
      editor.root.setAttribute('autocorrect', 'off');
      editor.root.setAttribute('autocomplete', 'off');
      editor.root.setAttribute('data-gramm', 'false');
      return true;
    };
    if (!apply()) { const t = setTimeout(apply, 0); return () => clearTimeout(t); }
  }, []);

  useEffect(() => {
    const handler = () => {
      requestAnimationFrame(() => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        const active = document.activeElement;
        if (active && active !== document.body) {
          if (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA') return;
          if (active.isContentEditable && active !== quill.root) return;
        }
        const scrollEl = scrollContainerRef.current;
        const savedScroll = scrollEl?.scrollTop ?? 0;
        if (quill.root?.focus) {
          quill.root.focus({ preventScroll: true });
        } else {
          quill.focus();
        }
        if (scrollEl) scrollEl.scrollTop = savedScroll;
      });
    };
    window.addEventListener('notesplus-focus-editor', handler);
    return () => window.removeEventListener('notesplus-focus-editor', handler);
  }, []);

  // Quill 1.x: scroll yalnızca editör container'ında (react-quill bundled Quill 1.3.7)
  useEffect(() => {
    const SCROLL_PAD_TOP = 48;
    const SCROLL_PAD_BOTTOM = 120;

    const setup = () => {
      const editor = quillRef.current?.getEditor();
      const scrollEl = scrollContainerRef.current;
      if (!editor?.selection || typeof editor.selection.scrollIntoView !== 'function' || !scrollEl) {
        return false;
      }

      editor.scrollingContainer = scrollEl;

      const originalSelectionScroll = editor.selection.scrollIntoView.bind(editor.selection);

      editor.selection.scrollIntoView = (container) => {
        const scrollingContainer = container || scrollEl;
        const range = editor.selection.lastRange;
        if (!range) return;

        const bounds = editor.selection.getBounds(range.index, range.length);
        if (!bounds) return;

        const scrollBounds = scrollingContainer.getBoundingClientRect();
        const viewTop = scrollBounds.top + SCROLL_PAD_TOP;
        const viewBottom = scrollBounds.bottom - SCROLL_PAD_BOTTOM;

        if (bounds.bottom > viewBottom) {
          scrollingContainer.scrollTop += bounds.bottom - viewBottom;
        } else if (bounds.top < viewTop) {
          scrollingContainer.scrollTop -= viewTop - bounds.top;
        }
      };

      if (typeof editor.scrollIntoView === 'function') {
        editor.scrollIntoView = () => editor.selection.scrollIntoView(scrollEl);
      }

      // Paste sırasında scroll sıçramasını önle: pozisyonu kaydet, paste sonrası restore et
      const handlePaste = () => {
        const savedTop = scrollEl.scrollTop;
        const savedWindowY = window.scrollY;
        requestAnimationFrame(() => {
          // Scroll yukarı sıçradıysa geri al
          if (scrollEl.scrollTop < savedTop - 20) {
            scrollEl.scrollTop = savedTop;
          }
          if (window.scrollY < savedWindowY - 20) {
            window.scrollTo(0, savedWindowY);
          }
        });
      };
      editor.root.addEventListener('paste', handlePaste, true);

      return () => {
        editor.selection.scrollIntoView = originalSelectionScroll;
        editor.scrollingContainer = editor.root;
        editor.root.removeEventListener('paste', handlePaste, true);
      };
    };

    let cleanup;
    const result = setup();
    if (typeof result === 'function') {
      cleanup = result;
    } else {
      const t = setTimeout(() => { cleanup = setup() || undefined; }, 0);
      return () => { clearTimeout(t); if (typeof cleanup === 'function') cleanup(); };
    }
    return cleanup;
  }, [quillRemountKey, note?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDateSafe = useCallback((value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return format(d, 'dd MMM yyyy, HH:mm', { locale: dateLocale });
  }, [dateLocale]);

  if (!note) return null;

  const createdAtLabel = formatDateSafe(note?.createdAt);
  const updatedAtLabel = formatDateSafe(note?.updatedAt);
  const canCopyOrCut = contextMenu.selectionLength > 0;

  /* ─── Derived toolbar state ─── */
  const currentHeading = activeFormats.header || false;
  const currentSize = activeFormats.size || '';
  const currentFont = activeFormats.font || '';
  const fontLabel = FONT_OPTIONS.find(f => f.value === currentFont)?.label || t('editor.toolbar.fontFallback');

  return (
    <div
      onContextMenu={handleContextMenu}
      className="rich-note-editor-root h-full flex flex-col rounded-md overflow-hidden transition-colors border border-slate-200 dark:border-0 bg-white dark:bg-[#0d1424] shadow-sm dark:shadow-none"
    >
      {/* ─── Top header: back + dates + save ─── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-white/[0.06] bg-white dark:bg-[#0d1424] transition-colors flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="p-2 rounded-lg border border-slate-200 dark:border-0 dark:bg-[#161f31] hover:border-slate-300 dark:hover:bg-[#1a2640] text-slate-600 dark:text-[#c8d8eb] hover:text-slate-900 dark:hover:text-white bg-slate-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          {updatedAtLabel && (
            <span className="text-[11px] text-slate-500 dark:text-[#7a96b0]">
              {t('editor.lastEdited', { date: updatedAtLabel })}
            </span>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {!isEditorReadOnly && (
            <div className="flex items-center gap-2">
              {justSaved && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-[#1a2d47] border border-[#3d8ef0]/40 text-[#3d8ef0]">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#3d8ef0]" />
                  {t('editor.saveButton.saved')}
                </div>
              )}
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-sm font-medium transition-colors ${
                  isSaving ? 'bg-blue-400 cursor-wait' : 'bg-[#2563eb] hover:bg-[#3d8ef0]'
                }`}
              >
                {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {isSaving ? t('editor.saveButton.saving') : t('editor.saveButton.idle')}
              </button>
            </div>
          )}
          {readOnly && (
            <span className="text-xs text-amber-600 dark:text-amber-400 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              {t('editor.readOnlyBanner')}
            </span>
          )}
        </div>
      </div>

      {/* ─── TOOLBAR ─── */}
      {!isEditorReadOnly && (
        <div className="toolbar-root flex-shrink-0 bg-[#0d1424] border-b border-white/[0.06] px-2 py-1.5 flex flex-wrap items-center gap-x-0.5 gap-y-1">

          {/* History */}
          <TBtn title={t('editor.toolbar.undo')} onClick={handleUndo}><Undo2 className="w-3.5 h-3.5" /></TBtn>
          <TBtn title={t('editor.toolbar.redo')} onClick={handleRedo}><Redo2 className="w-3.5 h-3.5" /></TBtn>

          <TDiv />

          {/* Heading — icon trigger */}
          <ToolbarDropdown
            width="w-16"
            value={currentHeading}
            options={headingOptions}
            onChange={(v) => applyHeading(v)}
            renderLabel={(v) => (
              <span className="flex items-center gap-1">
                <Heading className="w-3.5 h-3.5 flex-shrink-0 opacity-80" />
                {v ? <span className="text-[10px] font-bold leading-none">{v}</span> : null}
              </span>
            )}
            renderOption={(opt) => (
              <span style={{ fontSize: opt.value ? `${18 - (opt.value - 1) * 2}px` : '13px', fontWeight: opt.value ? 700 : 400 }}>
                {opt.label}
              </span>
            )}
          />

          {/* Font */}
          <ToolbarDropdown
            width="w-32"
            value={currentFont}
            options={FONT_OPTIONS}
            label={fontLabel}
            renderLabel={(v) => FONT_OPTIONS.find(f => f.value === v)?.label || t('editor.toolbar.fontFallback')}
            onChange={(v) => applyFont(v)}
            renderOption={(opt) => (
              <span style={{ fontFamily: opt.value }}>{opt.label}</span>
            )}
          />

          {/* Size — icon + number */}
          <ToolbarDropdown
            width="w-20"
            value={currentSize}
            options={SIZE_OPTIONS}
            onChange={(v) => applySize(v)}
            renderLabel={(v) => {
              const lbl = SIZE_OPTIONS.find(s => s.value === v)?.label;
              return (
                <span className="flex items-center gap-1">
                  <Type className="w-3 h-3 flex-shrink-0 opacity-70" />
                  <span>{lbl || '—'}</span>
                </span>
              );
            }}
          />

          <TDiv />

          {/* Bold / Italic / Underline / Strikethrough */}
          <TBtn title={t('editor.toolbar.bold')} active={!!activeFormats.bold} onClick={() => toggleFormat('bold')}><Bold className="w-3.5 h-3.5" /></TBtn>
          <TBtn title={t('editor.toolbar.italic')} active={!!activeFormats.italic} onClick={() => toggleFormat('italic')}><Italic className="w-3.5 h-3.5" /></TBtn>
          <TBtn title={t('editor.toolbar.underline')} active={!!activeFormats.underline} onClick={() => toggleFormat('underline')}><Underline className="w-3.5 h-3.5" /></TBtn>
          <TBtn title={t('editor.toolbar.strikethrough')} active={!!activeFormats.strike} onClick={() => toggleFormat('strike')}><Strikethrough className="w-3.5 h-3.5" /></TBtn>

          <TDiv />

          {/* Text color */}
          <ColorPicker
            colors={TEXT_COLORS}
            value={textColor}
            onChange={applyTextColor}
            onClear={() => applyTextColor('')}
            title={t('editor.toolbar.textColor')}
            triggerIcon={<Type className="w-3.5 h-3.5" />}
          />

          {/* Background / highlight color */}
          <ColorPicker
            colors={BG_COLORS}
            value={bgColor}
            onChange={applyBgColor}
            onClear={() => applyBgColor('')}
            title={t('editor.toolbar.bgColor')}
            triggerIcon={<Highlighter className="w-3.5 h-3.5" />}
          />

          <TDiv />

          {/* Lists */}
          <TBtn title={t('editor.toolbar.bulletList')} active={activeFormats.list === 'bullet'} onClick={() => toggleList('bullet')}>
            <List className="w-3.5 h-3.5" />
          </TBtn>
          <TBtn title={t('editor.toolbar.numberedList')} active={activeFormats.list === 'ordered'} onClick={() => toggleList('ordered')}>
            <ListOrdered className="w-3.5 h-3.5" />
          </TBtn>

          <TDiv />

          {/* Alignment */}
          <TBtn title={t('editor.toolbar.alignLeft')} active={!activeFormats.align || activeFormats.align === 'left'} onClick={() => applyAlign(false)}><AlignLeft className="w-3.5 h-3.5" /></TBtn>
          <TBtn title={t('editor.toolbar.alignCenter')} active={activeFormats.align === 'center'} onClick={() => applyAlign('center')}><AlignCenter className="w-3.5 h-3.5" /></TBtn>
          <TBtn title={t('editor.toolbar.alignRight')} active={activeFormats.align === 'right'} onClick={() => applyAlign('right')}><AlignRight className="w-3.5 h-3.5" /></TBtn>
          <TBtn title={t('editor.toolbar.alignJustify')} active={activeFormats.align === 'justify'} onClick={() => applyAlign('justify')}><AlignJustify className="w-3.5 h-3.5" /></TBtn>

          <TDiv />

          {/* Link */}
          <TBtn title={t('editor.toolbar.addLink')} active={!!activeFormats.link} onClick={openLinkModal}><Link2 className="w-3.5 h-3.5" /></TBtn>

          {/* Blockquote */}
          <TBtn title={t('editor.toolbar.blockquote')} active={!!activeFormats.blockquote} onClick={() => toggleBlockFormat('blockquote')}><Quote className="w-3.5 h-3.5" /></TBtn>

          {/* Code block */}
          <TBtn title={t('editor.toolbar.codeBlock')} active={!!activeFormats['code-block']} onClick={() => toggleBlockFormat('code-block')}><Code className="w-3.5 h-3.5" /></TBtn>

          {/* HR */}
          <TBtn title={t('editor.toolbar.horizontalRule')} onClick={insertHR}><Minus className="w-3.5 h-3.5" /></TBtn>

          <TDiv />

          {/* Print / PDF / Share */}
          <TBtn title={t('editor.toolbar.print')} onClick={handlePrint}><Printer className="w-3.5 h-3.5" /></TBtn>
          <TBtn title={t('editor.toolbar.savePdf')} onClick={handlePDF}><FileDown className="w-3.5 h-3.5" /></TBtn>
          <TBtn title={t('editor.toolbar.share')} onClick={handleShare}><Share2 className="w-3.5 h-3.5" /></TBtn>

          {/* Save status indicator — sabit genişlik, toolbar yüksekliği titremesin */}
          <div className="ml-auto flex items-center gap-1.5 text-[11px] text-[#7a96b0] px-1 min-w-[7.5rem] justify-end shrink-0">
            {saveStatus === 'saving' && <><Loader2 className="w-3 h-3 animate-spin text-[#3d8ef0]" /><span>{t('editor.saveStatus.saving')}</span></>}
            {saveStatus === 'draft' && <span className="text-amber-400">⚠ {t('editor.saveStatus.draft')}</span>}
            {saveStatus === 'offline' && <span className="text-amber-400">{t('editor.saveStatus.offline')}</span>}
          </div>
        </div>
      )}

      {/* ─── Editor scroll area ─── */}
      <div
        ref={scrollContainerRef}
        className="rich-note-editor-scroll flex-1 overflow-auto px-5 pb-6 transition-colors bg-white dark:bg-[#0d1424] focus:outline-none focus:ring-0"
        onContextMenu={handleContextMenu}
      >
        <QuillLoadErrorBoundary
          onError={() => setQuillLoadFailed(true)}
          fallback={
            <div className="text-sm text-amber-700 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2 mt-4">
              {t('editor.loadErrorFallback')}
            </div>
          }
        >
          <QuillEditor
            key={`${note?.id ?? 'none'}-${refreshKey}-${quillRemountKey}`}
            ref={quillRef}
            defaultValue={initialContentRef.current}
            onChange={isEditorReadOnly ? undefined : handleChange}
            modules={isEditorReadOnly ? { toolbar: false } : modules}
            formats={EDITOR_FORMATS}
            placeholder=""
            theme="snow"
            spellCheck={false}
            className="rich-note-editor"
            readOnly={isEditorReadOnly}
          />
        </QuillLoadErrorBoundary>
      </div>

      {/* ─── Link modal ─── */}
      {linkModal.visible && createPortal(
        <div className="fixed inset-0 z-[99998] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-[#0d1424] border border-white/[0.09] rounded-2xl shadow-[0_24px_64px_rgba(0,0,0,0.7)] p-5 w-[360px]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-[#e4edf7] font-medium">
                <Link2 className="w-4 h-4 text-[#3d8ef0]" />
                {t('editor.linkModal.title')}
              </div>
              <button type="button" onClick={() => setLinkModal({ visible: false, url: '' })} className="text-[#7a96b0] hover:text-[#e4edf7] transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <input
              ref={linkInputRef}
              type="text"
              value={linkModal.url}
              onChange={(e) => setLinkModal(prev => ({ ...prev, url: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); applyLink(linkModal.url); }
                if (e.key === 'Escape') setLinkModal({ visible: false, url: '' });
              }}
              placeholder="https://example.com"
              className="w-full bg-[#161f31] border border-white/[0.08] rounded-xl px-3 py-2 text-sm text-[#e4edf7] placeholder-[#7a96b0] outline-none focus:border-[#3d8ef0]/60 transition-colors mb-3"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => applyLink(linkModal.url)}
                className="flex-1 py-2 rounded-xl bg-[#2563eb] hover:bg-[#3d8ef0] text-white text-sm font-medium transition-colors"
              >
                {t('editor.linkModal.apply')}
              </button>
              {activeFormats.link && (
                <button
                  type="button"
                  onClick={() => applyLink('')}
                  className="px-3 py-2 rounded-xl bg-white/[0.05] hover:bg-white/[0.1] text-[#c8d8eb] text-sm transition-colors"
                >
                  {t('editor.linkModal.remove')}
                </button>
              )}
              <button
                type="button"
                onClick={() => setLinkModal({ visible: false, url: '' })}
                className="px-3 py-2 rounded-xl bg-white/[0.05] hover:bg-white/[0.1] text-[#c8d8eb] text-sm transition-colors"
              >
                {t('common:cancel')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ─── Context menu ─── */}
      {contextMenu.visible && typeof document !== 'undefined' && createPortal(
        <div
          ref={contextMenuRef}
          className="note-context-menu"
          style={{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }}
          role="menu"
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onContextMenu={(e) => e.preventDefault()}
        >
          {isElectron && contextMenu.linkUrl && (
            <>
              <button
                type="button"
                className="note-context-menu__item"
                onClick={() => { window.api.openExternal(contextMenu.linkUrl); closeContextMenu(); }}
                role="menuitem"
              >
                <span>{t('editor.contextMenu.openInBrowser')}</span>
              </button>
              <div className="note-context-menu__separator" />
            </>
          )}
          <button type="button" className="note-context-menu__item" onClick={handleCopy} disabled={!canCopyOrCut} role="menuitem">
            <span>{t('editor.contextMenu.copy')}</span><span className="note-context-menu__shortcut">Ctrl+C</span>
          </button>
          <button type="button" className="note-context-menu__item" onClick={handlePaste} role="menuitem">
            <span>{t('editor.contextMenu.paste')}</span><span className="note-context-menu__shortcut">Ctrl+V</span>
          </button>
          <button type="button" className="note-context-menu__item" onClick={handleCut} disabled={!canCopyOrCut} role="menuitem">
            <span>{t('editor.contextMenu.cut')}</span><span className="note-context-menu__shortcut">Ctrl+X</span>
          </button>
          <div className="note-context-menu__separator" />
          <button type="button" className="note-context-menu__item" onClick={handleSelectAll} role="menuitem">
            <span>{t('editor.contextMenu.selectAll')}</span><span className="note-context-menu__shortcut">Ctrl+A</span>
          </button>
          <button type="button" className="note-context-menu__item" onClick={handlePastePlain} role="menuitem">
            <span>{t('editor.contextMenu.pastePlain')}</span><span className="note-context-menu__shortcut">Ctrl+Shift+V</span>
          </button>
          <div className="note-context-menu__separator" />
          <button type="button" className="note-context-menu__item" onClick={() => { handleUndo(); closeContextMenu(); }} role="menuitem">
            <span>{t('editor.contextMenu.undo')}</span><span className="note-context-menu__shortcut">Ctrl+Z</span>
          </button>
          <button type="button" className="note-context-menu__item" onClick={() => { handleRedo(); closeContextMenu(); }} role="menuitem">
            <span>{t('editor.contextMenu.redo')}</span><span className="note-context-menu__shortcut">Ctrl+Y</span>
          </button>
        </div>,
        document.body
      )}

      <style>{`
        .rich-note-editor .ql-container { border: none; }
        .rich-note-editor .ql-toolbar { border: none; }
        .rich-note-editor .ql-editor {
          min-height: 600px;
          padding: 20px 12px 120px;
          font-size: 16px;
          line-height: 1.8;
        }
        .rich-note-editor .ql-editor:focus,
        .rich-note-editor .ql-editor:focus-visible {
          box-shadow: none !important;
          outline: none !important;
        }
        .rich-note-editor .ql-container.ql-snow {
          border-radius: 0 0 12px 12px;
          background-color: white;
        }
        .rich-note-editor .ql-editor hr {
          border: none;
          border-top: 1px solid #e2e8f0;
          margin: 24px 0;
        }
        .rich-note-editor-scroll {
          scroll-padding-top: 48px;
          scroll-padding-bottom: 120px;
          scrollbar-width: thin;
          scrollbar-color: transparent transparent;
        }
        .dark .rich-note-editor-scroll,
        html[data-theme="midnight"] .rich-note-editor-scroll {
          scrollbar-color: #1a2640 transparent;
        }
        .dark .rich-note-editor-scroll:hover,
        html[data-theme="midnight"] .rich-note-editor-scroll:hover {
          scrollbar-color: #24354f transparent;
        }
        .rich-note-editor-scroll::-webkit-scrollbar { width: 10px; height: 10px; background: transparent; }
        .rich-note-editor-scroll::-webkit-scrollbar-track { background: transparent; }
        .dark .rich-note-editor-scroll::-webkit-scrollbar-thumb,
        html[data-theme="midnight"] .rich-note-editor-scroll::-webkit-scrollbar-thumb {
          background: #1a2640; border-radius: 5px; border: 2px solid transparent; background-clip: padding-box;
        }
        .dark .rich-note-editor-scroll::-webkit-scrollbar-thumb:hover,
        html[data-theme="midnight"] .rich-note-editor-scroll::-webkit-scrollbar-thumb:hover {
          background: #24354f; border-radius: 5px; border: 2px solid transparent; background-clip: padding-box;
        }
        .dark .rich-note-editor-root,
        html[data-theme="midnight"] .rich-note-editor-root {
          border: none !important; box-shadow: none !important;
        }
        html[data-theme="midnight"] .rich-note-editor .ql-container.ql-snow,
        .dark .rich-note-editor .ql-container.ql-snow {
          border: none !important; background-color: #0d1424 !important;
        }
        html[data-theme="midnight"] .rich-note-editor .ql-toolbar.ql-snow,
        .dark .rich-note-editor .ql-toolbar.ql-snow { border: none !important; }
        html[data-theme="midnight"] .rich-note-editor .ql-editor,
        .dark .rich-note-editor .ql-editor {
          color: #e8f0fb !important; background-color: #0d1424 !important;
        }
        html[data-theme="midnight"] .rich-note-editor .ql-editor.ql-blank::before,
        .dark .rich-note-editor .ql-editor.ql-blank::before { color: #c8d8eb !important; }
        html[data-theme="midnight"] .rich-note-editor .ql-editor hr,
        .dark .rich-note-editor .ql-editor hr {
          border: none; border-top: 1px solid rgba(255,255,255,0.1); margin: 20px 0;
        }
        /* Quill 1.x liste marker'ları — li::before counter (react-quill bundled Quill 1.3.7) */
        .rich-note-editor .ql-editor ol,
        .rich-note-editor .ql-editor ul {
          overflow: visible !important;
        }
        .rich-note-editor .ql-editor li {
          padding-left: 1.5em !important;
          position: relative !important;
          overflow: visible !important;
        }
        /* li::before: Quill 1.x'te liste numarası/noktası doğrudan li::before üzerinde */
        .rich-note-editor .ql-editor li::before {
          display: inline-block !important;
          margin-left: -1.5em !important;
          margin-right: 0.3em !important;
          text-align: right !important;
          white-space: nowrap !important;
          width: 1.2em !important;
          color: inherit !important;
        }
        /* Numaralı liste (ordered) — CSS counter */
        .rich-note-editor .ql-editor ol li:before {
          content: counter(list-0, decimal) '. ' !important;
        }
        .rich-note-editor .ql-editor ol li.ql-indent-1:before {
          content: counter(list-1, lower-alpha) '. ' !important;
        }
        .rich-note-editor .ql-editor ol li.ql-indent-2:before {
          content: counter(list-2, lower-roman) '. ' !important;
        }
        /* Madde işaretli liste (bullet) */
        .rich-note-editor .ql-editor ul > li::before {
          content: "\u2022" !important;
        }
        /* Midnight teması — liste marker rengi */
        html[data-theme="midnight"] .rich-note-editor .ql-editor li::before,
        .dark .rich-note-editor .ql-editor li::before {
          color: #e8f0fb !important;
        }
        html[data-theme="midnight"] .rich-note-editor .ql-editor blockquote,
        .dark .rich-note-editor .ql-editor blockquote {
          border-left: 3px solid #3d8ef0; color: #c8d8eb; padding-left: 1em;
          margin: 0.5em 0; font-style: italic; background: rgba(61,142,240,0.05);
          border-radius: 0 4px 4px 0;
        }
        html[data-theme="midnight"] .rich-note-editor .ql-editor a,
        .dark .rich-note-editor .ql-editor a { color: #3d8ef0 !important; text-decoration: underline; }
        html[data-theme="midnight"] .rich-note-editor .ql-editor a:hover,
        .dark .rich-note-editor .ql-editor a:hover { color: #5ca3f5 !important; }
        html[data-theme="midnight"] .rich-note-editor .ql-editor pre.ql-syntax,
        .dark .rich-note-editor .ql-editor pre.ql-syntax {
          background-color: #161f31 !important; border: 1px solid rgba(255,255,255,0.07);
          border-radius: 8px; color: #c8d8eb !important; padding: 1em;
          font-family: Consolas, 'Courier New', monospace; font-size: 13px;
        }
        html[data-theme="midnight"] .rich-note-editor .ql-editor .ql-align-justify,
        .dark .rich-note-editor .ql-editor .ql-align-justify { text-align: justify !important; }
        html[data-theme="midnight"] .rich-note-editor .ql-editor .ql-align-center,
        .dark .rich-note-editor .ql-editor .ql-align-center { text-align: center !important; }
        html[data-theme="midnight"] .rich-note-editor .ql-editor .ql-align-right,
        .dark .rich-note-editor .ql-editor .ql-align-right { text-align: right !important; }
        /* Toolbar scrollbar */
        .toolbar-root { scrollbar-width: none; }
        .toolbar-root::-webkit-scrollbar { display: none; }
        /* Context menu */
        .note-context-menu {
          position: fixed; z-index: 9999; min-width: 210px; padding: 0.35rem 0;
          border-radius: 12px; border: 1px solid rgba(148,163,184,0.3);
          background: rgba(255,255,255,0.95); box-shadow: 0 16px 40px rgba(15,23,42,0.22);
          backdrop-filter: blur(14px); animation: contextMenuFadeIn 0.12s ease-out;
        }
        html[data-theme="midnight"] .note-context-menu {
          background: #0d1424; border-color: rgba(255,255,255,0.08);
          box-shadow: 0 16px 48px rgba(0,0,0,0.6);
        }
        .note-context-menu__item {
          width: 100%; display: flex; align-items: center; justify-content: space-between;
          gap: 0.5rem; padding: 0.55rem 0.9rem; font-size: 0.85rem; font-weight: 500;
          color: #161f31; transition: background 0.15s ease, color 0.15s ease;
        }
        html[data-theme="midnight"] .note-context-menu__item { color: #c8d8eb; }
        .note-context-menu__item:hover:not(:disabled) { background: rgba(59,130,246,0.12); color: #1d4ed8; }
        html[data-theme="midnight"] .note-context-menu__item:hover:not(:disabled) {
          background: rgba(255,255,255,0.05); color: #e4edf7;
        }
        .note-context-menu__item:disabled { opacity: 0.45; cursor: not-allowed; }
        .note-context-menu__separator { height: 1px; margin: 0.25rem 0.65rem; background: rgba(148,163,184,0.25); }
        html[data-theme="midnight"] .note-context-menu__separator { background: rgba(255,255,255,0.07); }
        .note-context-menu__shortcut { font-size: 0.75rem; opacity: 0.6; }
        @keyframes contextMenuFadeIn {
          from { opacity: 0; transform: translateY(6px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default RichNoteEditor;
